import { useState } from 'react'
import { Button, Card, Form, Input, Modal, Tag, Tooltip, Typography } from 'antd'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Can } from '@/shared/auth/Can'
import { formatDateTime, utcTooltip } from '@/shared/lib/format'
import { OperationResultAlert } from '@/shared/ui/OperationResultAlert'
import { useOperationRunner } from '@/shared/ui/useOperationRunner'
import { fetchSelfcarePasswordStatus, setSelfcarePassword } from './selfcareApi'
import { submitOnValid } from '@/shared/lib/submitOnValid'

interface SelfcarePasswordActionProps {
  subscriberId: string
  /** Yopilgan abonentda tugma chizilmaydi — chaqiruvchi hal qiladi */
  disabled?: boolean
}

/**
 * Kabinet paroli — abonentning shaxsiy kabinetiga kirish paroli.
 *
 * ⚠️ BU RADIUS/PPPoE PAROLI EMAS (bu kartochkadagi "Asosiy ma'lumot"
 * bo'limidagi va `SubscriberActions`dagi "Parolni o'zgartirish" — o'sha
 * internetga ulanish paroli). Ikkalasi mustaqil maydon, mustaqil endpoint.
 * Shuning uchun bu ATAYLAB alohida Card: operator ikkitasini bitta tugma
 * deb adashtirmasin — aks holda "kabinet parolini o'zgartirdim, lekin
 * abonent internetga chiqolmay qoldi" (yoki aksincha) degan holat yuzaga
 * keladi.
 */
export function SelfcarePasswordAction({ subscriberId, disabled }: SelfcarePasswordActionProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [form] = Form.useForm<{ password: string }>()
  const runner = useOperationRunner()
  const queryClient = useQueryClient()

  const statusKey = ['selfcare', 'passwordStatus', subscriberId] as const
  const status = useQuery({
    queryKey: statusKey,
    queryFn: () => fetchSelfcarePasswordStatus(subscriberId),
    // ⚠️ Huquqsiz foydalanuvchida 403 keladi — bu XATO EMAS, kutilgan
    // holat. Qayta urinish faqat bekorga so'rov yuborardi.
    retry: false,
    /*
      Holat "o'rnatilgan / o'rnatilmagan" dan iborat va u faqat SHU
      kartadagi amaldan keyin o'zgaradi — o'sha yerda kesh qo'lda bekor
      qilinadi (`invalidateQueries(statusKey)`). Ya'ni umumiy 10 soniyalik
      chegara hech nima qo'shmasdi, faqat kartochka har ochilganda
      qo'shimcha so'rov yuborardi.
    */
    staleTime: 5 * 60_000,
  })

  // ⚠️ `undefined` (hali noma'lum) va `false` (aniq o'rnatilmagan) —
  // BOSHQA holatlar. Ikkalasini "o'rnatilmagan" deb ko'rsatish
  // operatorga yuklanish paytida noto'g'ri javob berardi.
  const isSet = status.data?.set

  const submit = () => {
    submitOnValid(form, async (values) => {
      const outcome = await runner.run(() => setSelfcarePassword(subscriberId, values.password))
      if (outcome.status !== 'FAILED') {
        setOpen(false)
        form.resetFields()
        void queryClient.invalidateQueries({ queryKey: statusKey })
      }
    })
  }

  return (
    <Card
      title={t('selfcare.title')}
      extra={
        isSet === undefined ? null : isSet ? (
          <Tooltip
            title={
              status.data?.updatedAt
                ? `${t('selfcare.setAt')}: ${formatDateTime(status.data.updatedAt)} (${utcTooltip(status.data.updatedAt)})`
                : undefined
            }
          >
            <Tag color="green">{t('selfcare.stateSet')}</Tag>
          </Tooltip>
        ) : (
          <Tag>{t('selfcare.stateNotSet')}</Tag>
        )
      }
    >
      <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
        {t('selfcare.explain')}
      </Typography.Paragraph>

      {runner.result ? (
        <div style={{ marginBottom: 16 }}>
          <OperationResultAlert result={runner.result} closable onClose={runner.clear} />
        </div>
      ) : null}

      {!disabled ? (
        <Can permission="subscriber.password.set">
          <Button onClick={() => setOpen(true)}>
            {isSet ? t('selfcare.buttonChange') : t('selfcare.button')}
          </Button>
        </Can>
      ) : null}

      <Modal
        open={open}
        title={isSet ? t('selfcare.modalTitleChange') : t('selfcare.modalTitle')}
        onCancel={() => setOpen(false)}
        onOk={submit}
        confirmLoading={runner.running}
        okText={t('common.save')}
        cancelText={t('common.cancel')}
        destroyOnHidden
      >
        <Typography.Paragraph type="warning">{t('selfcare.warning')}</Typography.Paragraph>
        {/*
          ⚠️ Parol allaqachon bor bo'lsa buni AYTISH shart: operator
          ishlab turgan parolni bilmasdan almashtirib qo'ymasin —
          abonent kabinetdan chiqib qoladi va sababini bilmaydi.
        */}
        {isSet ? (
          <Typography.Paragraph type="danger">{t('selfcare.overwriteWarning')}</Typography.Paragraph>
        ) : null}
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item
            name="password"
            label={t('selfcare.newPassword')}
            extra={t('selfcare.effectHint')}
            rules={[
              { required: true, message: t('operation.validation.passwordRequired') },
              { min: 4, max: 128, message: t('operation.validation.passwordLength') },
            ]}
          >
            <Input.Password autoFocus autoComplete="new-password" />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  )
}
